import React, { useState } from "react";
import styled from "styled-components";
import Text from "./Text";
import { styles } from "../styles/themes/style";

interface IPollDisplayProps {
  pollData: string;
}

interface IPollOption {
  text: string;
  votes: number;
  percentage: number;
  isSelected: boolean;
}

const PollDisplay: React.FC<IPollDisplayProps> = ({ pollData }) => {
  const [pollOptions, setPollOptions] = useState<IPollOption[]>([]);
  const [question, setQuestion] = useState<string>("");
  const [hasVoted, setHasVoted] = useState<boolean>(false);
  const [totalVotes, setTotalVotes] = useState<number>(0);

  React.useEffect(() => {
    parsePollData(pollData);
  }, [pollData]);

  const parsePollData = (pollString: string) => {
    try {
      // Format: "null#Question#Option1,Option2,Option3"
      const parts = pollString.split("#");
      if (parts.length >= 3) {
        const pollQuestion = parts[1];
        const optionsText = parts[2];

        setQuestion(pollQuestion);

        const options = optionsText.split(",").map((option) => ({
          text: option.trim(),
          votes: Math.floor(Math.random() * 50) + 1, // Mock votes for demo
          percentage: 0,
          isSelected: false,
        }));

        const total = options.reduce((sum, option) => sum + option.votes, 0);
        setTotalVotes(total);

        // Calculate percentages
        const optionsWithPercentages = options.map((option) => ({
          ...option,
          percentage: Math.round((option.votes / total) * 100),
        }));

        setPollOptions(optionsWithPercentages);
      }
    } catch (error) {
      console.error("Error parsing poll data:", error);
    }
  };

  const handleOptionClick = (optionIndex: number) => {
    if (hasVoted) return; // Prevent multiple votes

    setPollOptions((prev) =>
      prev.map((option, index) => ({
        ...option,
        isSelected: index === optionIndex,
        votes: index === optionIndex ? option.votes + 1 : option.votes,
      }))
    );

    setHasVoted(true);
    setTotalVotes((prev) => prev + 1);

    // Recalculate percentages
    setTimeout(() => {
      setPollOptions((prev) => {
        const newTotal = prev.reduce((sum, option) => sum + option.votes, 0);
        return prev.map((option) => ({
          ...option,
          percentage: Math.round((option.votes / newTotal) * 100),
        }));
      });
    }, 100);
  };

  if (!question || pollOptions.length === 0) {
    return null;
  }

  return (
    <PollContainer>
      <PollQuestion>{question}</PollQuestion>

      <PollOptionsContainer>
        {pollOptions.map((option, index) => (
          <PollOption
            key={index}
            onClick={() => handleOptionClick(index)}
            $isSelected={option.isSelected}
            $hasVoted={hasVoted}
          >
            <OptionText>{option.text}</OptionText>

            {hasVoted && (
              <VoteInfo>
                <VoteCount>{option.votes} votes</VoteCount>
                <VotePercentage>{option.percentage}%</VotePercentage>
              </VoteInfo>
            )}

            {hasVoted && (
              <ProgressBar>
                <ProgressFill $percentage={option.percentage} />
              </ProgressBar>
            )}
          </PollOption>
        ))}
      </PollOptionsContainer>

      <PollFooter>
        <TotalVotes>{totalVotes} votes</TotalVotes>
        {!hasVoted && <VotePrompt>Click to vote</VotePrompt>}
        {hasVoted && (
          <UndoButton
            onClick={() => {
              setHasVoted(false);
              setPollOptions((prev) =>
                prev.map((option) => ({
                  ...option,
                  isSelected: false,
                  votes: option.votes - (option.isSelected ? 1 : 0),
                }))
              );
              setTotalVotes((prev) => prev - 1);

              // Recalculate percentages after undo
              setTimeout(() => {
                setPollOptions((prev) => {
                  const newTotal = prev.reduce(
                    (sum, option) => sum + option.votes,
                    0
                  );
                  return prev.map((option) => ({
                    ...option,
                    percentage:
                      newTotal > 0
                        ? Math.round((option.votes / newTotal) * 100)
                        : 0,
                  }));
                });
              }, 100);
            }}
            title="Undo"
          >
            ↩️ Undo
          </UndoButton>
        )}
      </PollFooter>
    </PollContainer>
  );
};

const PollContainer = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: ${styles.colors.white};
  border: 1px solid ${styles.colors.black100};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const PollQuestion = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${styles.colors.black600};
  margin-bottom: 16px;
  line-height: 1.4;
`;

const PollOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const PollOption = styled.div<{ $isSelected: boolean; $hasVoted: boolean }>`
  position: relative;
  padding: 10px 16px;
  background-color: ${(props) =>
    props.$isSelected ? styles.colors.black200 : styles.colors.black100};
  border: 1px solid
    ${(props) =>
      props.$isSelected ? styles.colors.primary600 : styles.colors.black200};
  border-radius: 4px;
  cursor: ${(props) => (props.$hasVoted ? "default" : "pointer")};
  transition: all 0.2s ease;
  gap: 4px;
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  user-select: none;
  width: 100%;

  &:hover {
    background-color: ${(props) =>
      props.$hasVoted
        ? props.$isSelected
          ? styles.colors.black200
          : styles.colors.black100
        : props.$isSelected
        ? styles.colors.blue300
        : styles.colors.black100};
    border-color: ${(props) =>
      props.$hasVoted
        ? props.$isSelected
          ? styles.colors.primary600
          : styles.colors.black200
        : props.$isSelected
        ? styles.colors.blue400
        : styles.colors.primary600};
  }
`;

const OptionText = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${styles.colors.black600};
  margin-bottom: 2px;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VoteInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  width: 100%;
`;

const VoteCount = styled(Text)`
  font-size: 12px;
  color: ${styles.colors.black400};
`;

const VotePercentage = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: ${styles.colors.primary600};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: ${styles.colors.black100};
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
`;

const ProgressFill = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${(props) => props.$percentage}%;
  background-color: ${styles.colors.primary600};
  transition: width 0.3s ease;
  border-radius: 3px;
`;

const PollFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid ${styles.colors.black100};
`;

const TotalVotes = styled(Text)`
  font-size: 12px;
  color: ${styles.colors.black400};
`;

const VotePrompt = styled(Text)`
  font-size: 12px;
  color: ${styles.colors.primary600};
  font-style: italic;
`;

const UndoButton = styled.button`
  background-color: ${styles.colors.white};
  color: ${styles.colors.black300};
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${styles.colors.white};
  }

  &:focus {
    outline: none;
  }
`;

export default PollDisplay;
